/**
 * CetusEd Admin Console — District Audit Trail
 *
 * COMPLIANCE: FERPA §1232g(b)(4)(B) — All vendor support staff actions in a
 *   district MUST be audit-logged. This view shows the complete audit trail
 *   for a specific district, including staff actions, configuration changes,
 *   and data access events.
 *
 *   The audit log is immutable and hash-chain verified per the platform's
 *   security architecture. Each entry carries a checksum that links it to
 *   the previous entry in the chain.
 */

import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAdminAuditLog } from '../../api/hooks';
import { DataTable, type Column } from '../../ui/DataTable';
import type { AdminAuditEntry } from '../../api/hooks';

const ACTION_FILTERS = ['', 'district.created', 'district.config.updated', 'user.impersonated', 'sso.config.updated', 'sis.synced'];

export const DistrictAudit: React.FC = () => {
  const { districtId } = useParams<{ districtId: string }>();
  const [actionFilter, setActionFilter] = useState('');

  const { data: entries, isLoading, error } = useAdminAuditLog({
    ...(districtId ? { districtId } : {}),
    ...(actionFilter ? { action: actionFilter } : {}),
    limit: 100,
  });

  const columns: Column<AdminAuditEntry>[] = [
    {
      key: 'timestamp',
      header: 'Timestamp',
      sortable: true,
      render: (row) => new Date(row.timestamp).toLocaleString(),
    },
    { key: 'actorEmail', header: 'Actor', sortable: true },
    { key: 'action', header: 'Action', sortable: true },
    { key: 'resource', header: 'Resource' },
    { key: 'ipAddress', header: 'IP', width: '140px' },
  ];

  if (error) {
    return (
      <div className="admin-error" role="alert">
        Unable to load audit log.
      </div>
    );
  }

  return (
    <div className="admin-page">
      <nav className="breadcrumb">
        <Link to="/districts">Districts</Link>
        {districtId && <><Link to={`/districts/${districtId}`}>District</Link></>}
        / Audit Log
      </nav>

      <div className="page-header">
        <h1>Audit Log</h1>
        {districtId && (
          <div className="filter-group">
            <label htmlFor="action-filter" className="sr-only">Filter by action</label>
            <select
              id="action-filter"
              value={actionFilter}
              onChange={(e) => setActionFilter(e.target.value)}
            >
              {ACTION_FILTERS.map((a) => (
                <option key={a} value={a}>
                  {a || 'All Actions'}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      <DataTable
        columns={columns}
        data={entries || []}
        keyExtractor={(e) => e.id}
        isLoading={isLoading}
        emptyMessage="No audit entries found for the selected filters."
      />
    </div>
  );
};
